import { useAuthStore } from "@/lib/auth/AuthStore";
import { mixpanelEvent } from "@/lib/mixpanels/mixpanel";
import { HOME_PAGE, PASSES_LANDING } from "@/settings/constant";
import { useRouter } from "next/router";
import { useCallback, useEffect } from "react";
import { isSubjects } from "src/api/product";
import { DEFAULT_HOME_TYPE, DEFAULT_STUDENT_SUBJECT, DEFAULT_TEACHER_SUBJECT, useHomeSwitchStore } from "src/store/homeSwitch.store";
import useGetGnbMenu from "../queries/useGetGnbMenu";
/**
 * query string의 homeType이 유효한지 검증
 */ const isValidHomeType = (value)=>{
    return value === "teacher" || value === "student";
};
/**
 * user.job을 HomeType으로 변환
 * - student/parent → 학생홈, teacher/principal(및 그 외 university/etc/없음) → 선생님홈
 */ const getHomeTypeFromJob = (job)=>{
    return job === "student" || job === "parent" ? "student" : "teacher";
};
/**
 * HomeType에 따른 기본 subject 반환
 * - 선생님: user.mainSubject 또는 EN
 * - 학생: ALL
 */ const getSubjectForType = (type, userMainSubject)=>{
    if (type === "student") {
        return DEFAULT_STUDENT_SUBJECT;
    }
    // 선생님: mainSubject가 유효한 Subjects면 사용, 아니면 EN
    if (userMainSubject && isSubjects(userMainSubject)) {
        return userMainSubject;
    }
    return DEFAULT_TEACHER_SUBJECT;
};
/**
 * GNB 메뉴 상태 관리 훅
 *
 * 역할:
 * - 로그인/비로그인에 따른 homeType, subject 초기화
 * - query string (homeType, homeId) 우선 적용
 * - 토글 핸들러 제공
 * - GNB 메뉴 API 호출
 */ const useGNBMenu = ()=>{
    var _gnbMenuQuery_data;
    const router = useRouter();
    const { type: queryHomeType , homeId: queryHomeId , pageType: routePageType  } = router.query;
    // /passes/[pageType] 경로에서는 path 파라미터 pageType이 homeType의 권위 소스다.
    // (GNB 토글은 원래 ?type= 쿼리만 참조해, 이용권 학생 페이지에서 선생님으로 표시되던 버그 수정)
    const effectiveHomeType = router.pathname.startsWith(PASSES_LANDING) && isValidHomeType(routePageType) ? routePageType : queryHomeType;
    const { user , isLoggedIn  } = useAuthStore();
    const { homeType , subject , homeId , initializedWithLogin , setHomeState , setHomeId  } = useHomeSwitchStore();
    const gnbMenuQuery = useGetGnbMenu();
    // 초기화: homeType 소스(쿼리 ?type= 또는 /passes path 파라미터) 우선, 없으면 로그인 상태에 따라 설정
    useEffect(()=>{
        // homeType 소스가 유효하면 우선 적용
        if (isValidHomeType(effectiveHomeType)) {
            const newSubject = getSubjectForType(effectiveHomeType, user === null || user === void 0 ? void 0 : user.mainSubject);
            setHomeState(effectiveHomeType, newSubject);
            return;
        }
        // 같은 로그인 상태로 이미 초기화된 경우 스킵 (페이지 이동 시 유저 선택 유지)
        // 로그인 상태가 변경되면(로그인/로그아웃) 재초기화
        if (initializedWithLogin === isLoggedIn) return;
        // 로그인 상태 기반 초기화
        if (isLoggedIn && (user === null || user === void 0 ? void 0 : user.uid)) {
            const type = getHomeTypeFromJob(user.job);
            const subject = getSubjectForType(type, user.mainSubject);
            setHomeState(type, subject, isLoggedIn);
        } else if (!isLoggedIn) {
            setHomeState(DEFAULT_HOME_TYPE, DEFAULT_TEACHER_SUBJECT, isLoggedIn);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isLoggedIn,
        effectiveHomeType
    ]);
    // queryHomeId → store 동기화 (비공개 homeId 지원)
    useEffect(()=>{
        if (queryHomeId && typeof queryHomeId === "string") {
            setHomeId(queryHomeId);
        }
    }, [
        queryHomeId,
        setHomeId
    ]);
    // gnbMenuQuery → store homeId 동기화
    useEffect(()=>{
        var _gnbMenuQuery_data;
        if (queryHomeId) return;
        if (gnbMenuQuery.isSuccess && ((_gnbMenuQuery_data = gnbMenuQuery.data) === null || _gnbMenuQuery_data === void 0 ? void 0 : _gnbMenuQuery_data.homeId) && gnbMenuQuery.data.type === homeType) {
            var _gnbMenuQuery_data_homeNavigation;
            const isPublicHomeId = (_gnbMenuQuery_data_homeNavigation = gnbMenuQuery.data.homeNavigation) === null || _gnbMenuQuery_data_homeNavigation === void 0 ? void 0 : _gnbMenuQuery_data_homeNavigation.some((nav)=>nav.homeId === homeId);
            if (!homeId || !isPublicHomeId) {
                setHomeId(gnbMenuQuery.data.homeId);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        queryHomeId,
        (_gnbMenuQuery_data = gnbMenuQuery.data) === null || _gnbMenuQuery_data === void 0 ? void 0 : _gnbMenuQuery_data.homeId,
        gnbMenuQuery.isSuccess,
        homeType,
        homeId
    ]);
    // 토글 핸들러
    const handleToggle = useCallback((newType)=>{
        const newSubject = getSubjectForType(newType, user === null || user === void 0 ? void 0 : user.mainSubject);
        setHomeState(newType, newSubject);
        setHomeId("");
        window.scrollTo(0, 0);
        // MX-335: 홈 타입 토글 클릭 트래킹
        mixpanelEvent("Home Type Toggle Clicked", {
            "Toggle Type": newType === "teacher" ? "선생님" : "학생",
            Position: "GNB"
        });
        // /passes 페이지에서는 URL도 변경해야 SSR이 다시 실행됨
        if (router.pathname.startsWith(PASSES_LANDING)) {
            router.push("".concat(PASSES_LANDING, "/").concat(newType));
            return;
        }
        // 홈이 아닌 다른 페이지에서 토글 시 홈으로 이동 (쿼리 제거)
        if (router.pathname !== HOME_PAGE || Object.keys(router.query).length > 0) {
            router.push(HOME_PAGE);
        }
    }, [
        user === null || user === void 0 ? void 0 : user.mainSubject,
        router,
        setHomeState,
        setHomeId
    ]);
    return {
        homeType,
        subject,
        homeId,
        handleToggle,
        gnbMenuQuery,
        isLoggedIn
    };
};
export default useGNBMenu;
